import { useState, useCallback } from "react";
import type {
  FieldDefinition,
  FieldType,
  RichTextValue,
} from "@/types/template";
import { FIELD_TYPES } from "@/types/template";
import { SUPPORTED_FONTS } from "@/lib/fonts";

interface FieldSchemaEditorProps {
  fields: FieldDefinition[];
  onChange: (fields: FieldDefinition[]) => void;
  /** Which project the schema belongs to. SVG offers a type set without
   *  plain text / textarea / image / boolean and defaults new text fields to
   *  text-rich (platform policy: one text field type everywhere). Remotion
   *  keeps the text-rich-first behavior too. */
  projectKind?: "remotion" | "svg";
}

/** Field types offered for SVG templates. Platform policy makes text fields
 *  text-rich only (no plain "text"); the token-substitution model still has
 *  no use for multiline, images, or booleans. */
const SVG_FIELD_TYPES: FieldType[] = [
  "layer",
  "text-rich",
  "color",
  "color-alpha",
  "number",
  "slider",
  "knob",
  "select",
];

// Use RichTextValue from the canonical types module so the new optional
// bold/italic/underline flags don't have to be re-declared inline. Defaults
// for those flags are "absent" — we intentionally don't seed them as `false`
// so the on-disk JSON stays minimal.
const DEFAULT_FOR_TYPE: Record<
  FieldType,
  string | number | boolean | RichTextValue
> = {
  text: "",
  textarea: "",
  "text-rich": { text: "", font: "Rubik", fontSize: 64 },
  number: 0,
  slider: 50,
  knob: 50,
  color: "#ffffff",
  "color-alpha": "#ffffffff",
  image: "",
  select: "",
  boolean: false,
  layer: true,
};

export function FieldSchemaEditor({
  fields,
  onChange,
  projectKind = "remotion",
}: FieldSchemaEditorProps) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const isSvg = projectKind === "svg";

  const addField = useCallback(() => {
    const idx = fields.length + 1;
    // Both kinds default new text fields to text-rich: Remotion's backend
    // rejects NEW templates whose schema contains text/textarea fields, and
    // SVG now follows the same one-text-field-type policy (its source
    // references dotted tokens like `{{key.text}}` / `{{key.fontFamily}}`).
    const newField: FieldDefinition = {
      key: `field${idx}`,
      label: `Field ${idx}`,
      type: "text-rich",
      default: { text: "", font: "Rubik", fontSize: isSvg ? 48 : 64 },
      required: false,
    };
    onChange([...fields, newField]);
    setExpandedIdx(fields.length);
  }, [fields, onChange, isSvg]);

  const removeField = useCallback(
    (idx: number) => {
      const next = fields.filter((_, i) => i !== idx);
      onChange(next);
      setExpandedIdx(null);
    },
    [fields, onChange],
  );

  const updateField = useCallback(
    (idx: number, patch: Partial<FieldDefinition>) => {
      const next = fields.map((f, i) => (i === idx ? { ...f, ...patch } : f));
      onChange(next);
    },
    [fields, onChange],
  );

  const moveField = useCallback(
    (idx: number, dir: -1 | 1) => {
      const target = idx + dir;
      if (target < 0 || target >= fields.length) return;
      const next = [...fields];
      [next[idx], next[target]] = [next[target]!, next[idx]!];
      onChange(next);
      setExpandedIdx(target);
    },
    [fields, onChange],
  );

  const inputClasses =
    "w-full rounded-lg border border-line bg-surface px-2 py-1.5 text-xs text-ink focus:border-line-strong focus:outline-none";

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-ink-2">
          {fields.length} field{fields.length !== 1 ? "s" : ""}
        </span>
        <button
          type="button"
          onClick={addField}
          className="rounded-lg bg-accent px-3 py-1 text-xs font-medium text-accent-fg transition-colors hover:bg-accent-hover"
        >
          + Add Field
        </button>
      </div>

      {fields.length === 0 && (
        <div className="rounded-lg border border-dashed border-line px-4 py-8 text-center text-xs text-ink-3">
          No fields defined. Click "Add Field" to create one.
        </div>
      )}

      <div className="space-y-1">
        {fields.map((field, idx) => {
          const expanded = expandedIdx === idx;
          return (
            <div
              key={idx}
              className="rounded-lg border border-line bg-surface-2"
            >
              {/* Collapsed row */}
              <div
                className="flex cursor-pointer items-center gap-2 px-3 py-2"
                onClick={() => setExpandedIdx(expanded ? null : idx)}
              >
                <span className="text-[10px] text-ink-3">
                  {expanded ? "▼" : "▶"}
                </span>
                <span className="text-xs font-medium text-ink">
                  {field.label}
                </span>
                <span className="rounded bg-surface-3 px-1.5 py-0.5 text-[10px] text-ink-2">
                  {field.type}
                </span>
                <span className="font-mono text-[10px] text-ink-3">
                  {field.key}
                </span>
                <div className="flex-1" />
                {/* Move buttons */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); moveField(idx, -1); }}
                  disabled={idx === 0}
                  className="text-xs text-ink-3 hover:text-ink disabled:opacity-30"
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); moveField(idx, 1); }}
                  disabled={idx === fields.length - 1}
                  className="text-xs text-ink-3 hover:text-ink disabled:opacity-30"
                  title="Move down"
                >
                  ↓
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeField(idx); }}
                  className="text-xs text-danger/60 hover:text-danger"
                  title="Remove field"
                >
                  ✕
                </button>
              </div>

              {/* Expanded editor */}
              {expanded && (
                <div className="border-t border-line px-3 py-3">
                  <div className="grid grid-cols-2 gap-3">
                    {/* Key */}
                    <div>
                      <label className="mb-1 block text-[10px] font-medium text-ink-2">
                        Key (prop name)
                      </label>
                      <input
                        type="text"
                        value={field.key}
                        onChange={(e) => updateField(idx, { key: e.target.value.replace(/[^a-zA-Z0-9_]/g, "") })}
                        className={inputClasses}
                        placeholder="myField"
                      />
                    </div>
                    {/* Label */}
                    <div>
                      <label className="mb-1 block text-[10px] font-medium text-ink-2">
                        Label (display name)
                      </label>
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) => updateField(idx, { label: e.target.value })}
                        className={inputClasses}
                        placeholder="My Field"
                      />
                    </div>
                    {/* Type */}
                    <div>
                      <label className="mb-1 block text-[10px] font-medium text-ink-2">
                        Type
                      </label>
                      <select
                        value={field.type}
                        onChange={(e) => {
                          const newType = e.target.value as FieldType;
                          // Converting a string field to text-rich must keep
                          // the text (wiping it to "" is how templates lose
                          // their copy); the reverse keeps .text as string.
                          const carriedDefault =
                            newType === "text-rich" && typeof field.default === "string" && field.default
                              ? { text: field.default, font: "Rubik", fontSize: 64 }
                              : field.type === "text-rich" &&
                                  (newType === "text" || newType === "textarea") &&
                                  field.default &&
                                  typeof field.default === "object"
                                ? (field.default as RichTextValue).text
                                : DEFAULT_FOR_TYPE[newType];
                          updateField(idx, {
                            type: newType,
                            default: carriedDefault,
                            // Reset range/timing props for non-numeric types
                            ...(newType !== "slider" && newType !== "knob" && newType !== "number"
                              ? { min: undefined, max: undefined, step: undefined, timing: undefined }
                              : {}),
                            // Reset options for non-select types
                            ...(newType !== "select" ? { options: undefined } : {}),
                          });
                        }}
                        className={inputClasses}
                      >
                        {/* SVG: restrict to the plain-value set (always keep
                            the field's own current type so odd legacy values
                            stay editable). Remotion: text/textarea are
                            rejected on new templates; only offer them when the
                            field already is one (legacy templates remain
                            editable). */}
                        {FIELD_TYPES.filter((ft) =>
                          isSvg
                            ? SVG_FIELD_TYPES.includes(ft.value) ||
                              ft.value === field.type
                            : (ft.value !== "text" && ft.value !== "textarea") ||
                              ft.value === field.type,
                        ).map((ft) => (
                          <option key={ft.value} value={ft.value}>
                            {ft.label} — {ft.description}
                          </option>
                        ))}
                      </select>
                      {field.type === "text-rich" &&
                        (isSvg ? (
                          <p className="mt-1 text-[10px] leading-snug text-ink-3">
                            The SVG must reference dotted tokens like{" "}
                            <code className="text-ink-2">{`{{${field.key}.text}}`}</code>{" "}
                            /{" "}
                            <code className="text-ink-2">{`{{${field.key}.fontFamily}}`}</code>{" "}
                            — a bare{" "}
                            <code className="text-ink-2">{`{{${field.key}}}`}</code>{" "}
                            only yields the text.
                          </p>
                        ) : (
                          <p className="mt-1 text-[10px] leading-snug text-ink-3">
                            Component code must read{" "}
                            <code className="text-ink-2">{field.key}.text</code>{" "}
                            (and .font/.fontSize) — rendering the prop directly
                            crashes.
                          </p>
                        ))}
                    </div>
                    {/* Required */}
                    <div className="flex items-end gap-2 pb-1">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateField(idx, { required: e.target.checked })}
                        className="h-4 w-4 accent-accent"
                      />
                      <label className="text-[10px] font-medium text-ink-2">Required</label>
                    </div>
                    {/* Multi-line (text-rich only) */}
                    {field.type === "text-rich" && (
                      <div
                        className="flex items-end gap-2 pb-1"
                        title="Value may contain line breaks; the editor shows a resizable text box. The template must render breaks (pre-line / tspans) for them to show."
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(field.multiline)}
                          onChange={(e) =>
                            updateField(idx, { multiline: e.target.checked || undefined })
                          }
                          className="h-4 w-4 accent-accent"
                        />
                        <label className="text-[10px] font-medium text-ink-2">Multi-line</label>
                      </div>
                    )}
                    {/* Default value */}
                    <div className="col-span-2">
                      <label className="mb-1 block text-[10px] font-medium text-ink-2">
                        Default value
                      </label>
                      {field.type === "boolean" ? (
                        <select
                          value={String(field.default)}
                          onChange={(e) => updateField(idx, { default: e.target.value === "true" })}
                          className={inputClasses}
                        >
                          <option value="false">false</option>
                          <option value="true">true</option>
                        </select>
                      ) : field.type === "number" || field.type === "slider" || field.type === "knob" ? (
                        <input
                          type="number"
                          value={Number(field.default ?? 0)}
                          onChange={(e) => updateField(idx, { default: Number(e.target.value) })}
                          className={inputClasses}
                        />
                      ) : field.type === "text-rich" ? (
                        (() => {
                          const def: RichTextValue =
                            field.default && typeof field.default === "object"
                              ? (field.default as RichTextValue)
                              : { text: "", font: "Rubik", fontSize: 64 };
                          return (
                            <div className="grid grid-cols-3 gap-2">
                              <input
                                type="text"
                                value={def.text}
                                onChange={(e) =>
                                  updateField(idx, { default: { ...def, text: e.target.value } })
                                }
                                placeholder="Default text"
                                className={inputClasses}
                              />
                              <select
                                value={def.font}
                                onChange={(e) =>
                                  updateField(idx, { default: { ...def, font: e.target.value } })
                                }
                                className={inputClasses}
                              >
                                {SUPPORTED_FONTS.map((f) => (
                                  <option key={f.family} value={f.family}>
                                    {f.name}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="number"
                                value={def.fontSize}
                                onChange={(e) =>
                                  updateField(idx, {
                                    default: { ...def, fontSize: Number(e.target.value) },
                                  })
                                }
                                placeholder="Size"
                                className={inputClasses}
                                min={8}
                              />
                            </div>
                          );
                        })()
                      ) : (
                        <input
                          type="text"
                          value={String(field.default ?? "")}
                          onChange={(e) => updateField(idx, { default: e.target.value })}
                          className={inputClasses}
                        />
                      )}
                    </div>

                    {/* Min/max font size for text-rich */}
                    {field.type === "text-rich" && (
                      <>
                        <div>
                          <label className="mb-1 block text-[10px] font-medium text-ink-2">Min size (px)</label>
                          <input
                            type="number"
                            value={field.min ?? 8}
                            onChange={(e) => updateField(idx, { min: Number(e.target.value) })}
                            className={inputClasses}
                            min={1}
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-[10px] font-medium text-ink-2">Max size (px)</label>
                          <input
                            type="number"
                            value={field.max ?? 256}
                            onChange={(e) => updateField(idx, { max: Number(e.target.value) })}
                            className={inputClasses}
                            min={1}
                          />
                        </div>
                      </>
                    )}

                    {/* Range props for slider/knob/number */}
                    {(field.type === "slider" || field.type === "knob" || field.type === "number") && (
                      <>
                        <div>
                          <label className="mb-1 block text-[10px] font-medium text-ink-2">Min</label>
                          <input
                            type="number"
                            value={field.min ?? 0}
                            onChange={(e) => updateField(idx, { min: Number(e.target.value) })}
                            className={inputClasses}
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-[10px] font-medium text-ink-2">Max</label>
                          <input
                            type="number"
                            value={field.max ?? 100}
                            onChange={(e) => updateField(idx, { max: Number(e.target.value) })}
                            className={inputClasses}
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-[10px] font-medium text-ink-2">Step</label>
                          <input
                            type="number"
                            value={field.step ?? 1}
                            onChange={(e) => updateField(idx, { step: Number(e.target.value) })}
                            className={inputClasses}
                            step="any"
                          />
                        </div>
                      </>
                    )}

                    {/* Timing — numeric fields can drive the animation's
                        total duration (e.g. a countdown's totalSeconds).
                        Preview and render length both follow this. */}
                    {(field.type === "slider" || field.type === "knob" || field.type === "number") && (
                      <div className="col-span-2 rounded-lg border border-line bg-surface p-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={!!field.timing}
                            onChange={(e) =>
                              updateField(idx, {
                                timing: e.target.checked
                                  ? { unit: "seconds" }
                                  : undefined,
                              })
                            }
                            className="h-4 w-4 accent-accent"
                          />
                          <label className="text-[10px] font-medium text-ink-2">
                            Drives duration — the animation runs as long as this field says
                          </label>
                        </div>
                        {field.timing && (
                          <div className="mt-2 grid grid-cols-3 gap-2">
                            <div>
                              <label className="mb-1 block text-[10px] font-medium text-ink-2">
                                Value is in
                              </label>
                              <select
                                value={field.timing.unit}
                                onChange={(e) =>
                                  updateField(idx, {
                                    timing: {
                                      ...field.timing!,
                                      unit: e.target.value as NonNullable<
                                        typeof field.timing
                                      >["unit"],
                                    },
                                  })
                                }
                                className={inputClasses}
                              >
                                <option value="seconds">Seconds</option>
                                <option value="minutes">Minutes</option>
                                <option value="frames">Frames</option>
                              </select>
                            </div>
                            <div>
                              <label className="mb-1 block text-[10px] font-medium text-ink-2">
                                × Multiplier
                              </label>
                              <input
                                type="number"
                                value={field.timing.scale ?? 1}
                                onChange={(e) =>
                                  updateField(idx, {
                                    timing: {
                                      ...field.timing!,
                                      scale: Number(e.target.value) || 1,
                                    },
                                  })
                                }
                                className={inputClasses}
                                step="any"
                                title="Multiplier, e.g. seconds per item when this field is a count"
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-[10px] font-medium text-ink-2">
                                + Tail (seconds)
                              </label>
                              <input
                                type="number"
                                value={field.timing.offsetSeconds ?? 0}
                                onChange={(e) =>
                                  updateField(idx, {
                                    timing: {
                                      ...field.timing!,
                                      offsetSeconds: Number(e.target.value) || 0,
                                    },
                                  })
                                }
                                className={inputClasses}
                                step="any"
                                title="Extra hold after the timed portion, e.g. an outro after a countdown hits zero"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Options for select */}
                    {field.type === "select" && (
                      <div className="col-span-2">
                        <label className="mb-1 block text-[10px] font-medium text-ink-2">
                          Options (one per line)
                        </label>
                        <textarea
                          value={(field.options ?? []).join("\n")}
                          onChange={(e) => {
                            const opts = e.target.value.split("\n").filter((l) => l.trim());
                            updateField(idx, { options: opts });
                          }}
                          rows={3}
                          className={`${inputClasses} resize-none`}
                          placeholder="Option 1&#10;Option 2&#10;Option 3"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
